import React, { useMemo } from "react";
import { Popover, Table, Typography, theme, Button } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { AccessLevel } from "../../../interfaces/accessLevel.enum";
import { routes } from "../../../routes/AppRoutes";
import { IRouteConfig } from "../../../interfaces/routeConfig.interface";

type RoleMeta = { name: string; description: string };

const roleMeta: Record<AccessLevel, RoleMeta> = {
  [AccessLevel.KlearlySuperAdmin]: {
    name: "Klearly Super Admin",
    description:
      "Full access to all features, including Admins and Development.",
  },
  [AccessLevel.KlearlyAdmin]: {
    name: "Klearly Admin",
    description:
      "Manage businesses, partners, notifications, reports, terminals, sales, and bucket deals.",
  },
  [AccessLevel.CoreTechEmployee]: {
    name: "CoreTech Employee",
    description: "Operate terminals and perform coupling tasks.",
  },
  [AccessLevel.Partner]: {
    name: "Partner",
    description: "Access partner merchants overview and new merchant deals.",
  },
  [AccessLevel.KlearlyAnalytics]: {
    name: "Klearly Analytics",
    description: "View analytics and reporting dashboards.",
  },
  [AccessLevel.KlearlySalesSuperAdmin]: {
    name: "Klearly Sales Super Admin",
    description: "Oversee sales organization and advanced sales operations.",
  },
  [AccessLevel.KlearlySalesAdmin]: {
    name: "Klearly Sales Admin",
    description: "Manage day-to-day sales operations.",
  },
};

const orderedRoles: AccessLevel[] = [
  AccessLevel.KlearlySuperAdmin,
  AccessLevel.KlearlyAdmin,
  AccessLevel.CoreTechEmployee,
  AccessLevel.Partner,
  AccessLevel.KlearlyAnalytics,
  AccessLevel.KlearlySalesSuperAdmin,
  AccessLevel.KlearlySalesAdmin,
];

export default function RoleInfo() {
  const { token } = theme.useToken();
  const roleEntries = useMemo(() => {
    const leafRoutes: {
      label: string;
      access: AccessLevel[];
      protected?: boolean;
    }[] = [];

    const pushRoute = (route: IRouteConfig) => {
      if (route.children && route.children.length) {
        route.children.forEach(pushRoute);
        return;
      }
      const rawLabel = route.label || route.path || "";
      const label = rawLabel.startsWith("/")
        ? rawLabel.slice(1) || "/"
        : rawLabel;
      if (!route.accessLevel || route.accessLevel.length === 0) return;
      if (route.isProtected === false) return; // skip login/mfa and other explicit public pages
      leafRoutes.push({
        label,
        access: route.accessLevel,
        protected: route.isProtected,
      });
    };

    routes.forEach(pushRoute);

    const map = new Map<AccessLevel, Set<string>>();
    Object.values(AccessLevel).forEach((lvl) =>
      map.set(lvl as AccessLevel, new Set<string>())
    );

    leafRoutes.forEach(({ label, access }) => {
      access.forEach((lvl) => {
        const set = map.get(lvl);
        if (set) set.add(label);
      });
    });

    return orderedRoles.map((lvl) => ({
      level: lvl,
      pages: Array.from(map.get(lvl) || new Set<string>()).sort(),
    }));
  }, []);

  const columns = [
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
      width: 180,
      render: (text: string) => (
        <Typography.Text strong>{text}</Typography.Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (text: string) => (
        <Typography.Text type="secondary">{text}</Typography.Text>
      ),
    },
    {
      title: "Pages",
      dataIndex: "pages",
      key: "pages",
      render: (pages: string[]) => (
        <Typography.Text>
          {pages && pages.length ? pages.join(", ") : "-"}
        </Typography.Text>
      ),
    },
  ];

  const dataSource = roleEntries
    .map(({ level, pages }) => {
      const meta = roleMeta[level];
      if (!meta) return null;
      return {
        key: level as string,
        roleName: meta.name,
        description: meta.description,
        pages,
      };
    })
    .filter(Boolean) as Array<{
    key: string;
    roleName: string;
    description: string;
    pages: string[];
  }>;

  const content = (
    <div style={{ maxWidth: 720 }}>
      <Table
        size="small"
        columns={columns as any}
        dataSource={dataSource}
        pagination={false}
      />
    </div>
  );

  return (
    <Popover
      placement="left"
      title={<Typography.Text strong>Roles and page access</Typography.Text>}
      content={content}
      trigger={["hover", "click"]}
    >
      <Button
        type="text"
        shape="circle"
        icon={<InfoCircleOutlined />}
        aria-label="Show role access info"
        style={{ color: token.colorTextSecondary }}
      />
    </Popover>
  );
}
